import { type ReactElement, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { styled } from 'styled-components';

import { ScreenLoader } from '@dutchie-pos/components';
import { LD, useLDFlag } from '@dutchie-pos/utils';

import type { CartParams } from 'src/app/types/params';
import { MissingImageIcon } from 'src/assets/missing-image';
import { Button } from 'src/components/button';
import { LinkButton } from 'src/components/link-button';
import { colors } from 'src/css/theme';
import { useDeliveryInfo } from 'src/queries/curbside/get-delivery-info';
import type { DeliveryInfo } from 'src/queries/curbside/types';
import { useActiveCustomer } from 'src/queries/curbside/use-carts';
import { useGuestIdentificationsQuery } from 'src/queries/v2/cart/guest-identifications';
import { useSecondaryIdentificationQuery } from 'src/queries/v2/cart/secondary-identification';
import { getManifestBytes } from 'src/queries/v2/manifest/get-manifest-bytes';
import { Path } from 'src/store/slices/user';
import { useAppSelector } from 'src/utils';
import { useBaseAPIParams } from 'src/utils/hooks/useBaseAPIParams';

import { ContentTitle } from '../../content-title';
import { useDocumentViewer } from '../../document-viewer';
import { headerHeight } from '../../header';
import { getFirstSecondaryIdentificationImage, getIdentificationDisplayImage } from './primary-identification';
import { ViewProfileDrawer } from './view-profile-drawer';

export const getAddressQuery = (deliveryInfo: DeliveryInfo | undefined) => encodeURI(getFormattedAddress(deliveryInfo));

export const getFormattedAddress = (deliveryInfo: DeliveryInfo | undefined) => {
  let address = deliveryInfo?.Address?.street ?? '';
  address += deliveryInfo?.Address?.city ? `, ${deliveryInfo.Address.city}` : '';
  address += deliveryInfo?.Address?.state ? `, ${deliveryInfo.Address.state}` : '';
  address += deliveryInfo?.Address?.postal_code ? ` ${deliveryInfo.Address.postal_code}` : '';
  return address;
};

export type CartHeaderProps = {
  onGoBack: () => void;
};

export const CartHeader = (props: CartHeaderProps): ReactElement => {
  // Global state
  const baseAPIParams = useBaseAPIParams();
  const selectedPath = useAppSelector((state) => state.user.selectedPath);
  const selectedRouteId = useAppSelector((state) => state.user.selectedRouteId);

  // Local state
  const [isFetchingManifest, setFetchingManifest] = useState(false);
  const [isViewProfileDrawerOpen, setViewProfileDrawerOpen] = useState(false);

  // Flags
  const isShowDriversLicenseEnabled = useLDFlag(LD.MOBILE_CHECKOUT_SHOW_DRIVERS_LICENSE, false);
  const isPrimaryIdentificationByCustomerTypeEnabled = useLDFlag(LD.PRIMARY_IDENTIFICATION_BY_CUSTOMER_TYPE, false);
  const isIdentificationPhotoPreservationV2Enabled = useLDFlag(LD.IDENTIFICATION_PHOTO_PRESERVATION_V2, false);

  // Hooks
  const { data: activeCustomer } = useActiveCustomer();
  const documentViewer = useDocumentViewer();
  const { shipmentId } = useParams<CartParams>();
  const { data: deliveryInfo } = useDeliveryInfo();
  const guestId = activeCustomer?.Guest_id ?? 0;
  const useTypedPrimaryIdentification =
    isShowDriversLicenseEnabled &&
    isPrimaryIdentificationByCustomerTypeEnabled &&
    isIdentificationPhotoPreservationV2Enabled;
  const { data: guestIdentificationDetails } = useGuestIdentificationsQuery({
    guestId,
    options: { enabled: useTypedPrimaryIdentification },
  });
  const activeCustomerIsMedical =
    activeCustomer && 'IsMedical' in activeCustomer ? activeCustomer.IsMedical : undefined;
  const effectiveIsMedical = activeCustomerIsMedical ?? guestIdentificationDetails?.IsMedical ?? false;
  const typedIdentificationImage = guestIdentificationDetails
    ? getIdentificationDisplayImage({
        IsMedical: effectiveIsMedical,
        identifications: guestIdentificationDetails.identifications ?? [],
      })
    : null;
  // Last-resort fallback: only fetch secondary IDs once guest details has loaded and neither primary slot has an
  // image. The endpoint downloads every secondary image, so it must not run for customers who already have one.
  const needsSecondaryIdentificationFallback =
    useTypedPrimaryIdentification && !!guestIdentificationDetails && !typedIdentificationImage;
  const { data: secondaryIdentifications } = useSecondaryIdentificationQuery({
    guestId,
    options: {
      enabled: isShowDriversLicenseEnabled && (!useTypedPrimaryIdentification || needsSecondaryIdentificationFallback),
    },
  });

  // Computed
  const dateOfBirth = new Date(activeCustomer?.Dob ?? '');

  // Handlers
  const getAndShowManifest = async () => {
    setFetchingManifest(true);
    try {
      const manifestData = await getManifestBytes(Number(shipmentId), baseAPIParams);
      if (manifestData) {
        documentViewer.show(manifestData);
      }
    } finally {
      setFetchingManifest(false);
    }
  };

  const navigate = useNavigate();
  // seems like if we do not have an active customer but this page is refreshed we should grab that info not redirect...
  if (!activeCustomer) {
    navigate(
      selectedPath === Path.Delivery && selectedRouteId ? `/route/delivery/${String(selectedRouteId)}` : '/carts',
    );
  }

  const dob = `${String(dateOfBirth.getMonth() + 1)}/${String(dateOfBirth.getDate())}/${String(dateOfBirth.getFullYear())}`;
  const addressUrl = `https://www.google.com/maps/dir/?api=1&destination=${getAddressQuery(deliveryInfo)}`;

  const idImage = useTypedPrimaryIdentification
    ? (typedIdentificationImage ??
      (needsSecondaryIdentificationFallback ? getFirstSecondaryIdentificationImage(secondaryIdentifications) : null))
    : (secondaryIdentifications?.[0]?.Image ?? null);

  return (
    <>
      <ScreenLoader speed='fast' top={headerHeight} visible={isFetchingManifest} />
      <Container>
        <ContentTitle onGoBack={props.onGoBack}>{activeCustomer?.FullName}</ContentTitle>

        {isShowDriversLicenseEnabled && (
          <BottomContainer>
            <DetailsContainer>
              {idImage ? (
                <Image src={`data:image/png;base64, ${idImage}`} alt={`${activeCustomer?.FullName ?? ''} ID`} />
              ) : (
                <PlaceholderBox>
                  <MissingImageIcon />
                </PlaceholderBox>
              )}
              <SecondaryDetailsContainer>
                <Detail>
                  {activeCustomer?.Phone && (
                    <>
                      Phone #: <a href={`tel:${activeCustomer?.Phone}`}>{activeCustomer?.Phone}</a>{' '}
                    </>
                  )}
                </Detail>
                <Detail>DOB: {dob}</Detail>
                <Row>TxnID: {shipmentId}</Row>
                {selectedPath === Path.Delivery && (
                  <Row>
                    <a href={addressUrl}>{getFormattedAddress(deliveryInfo)}</a>
                  </Row>
                )}
                {deliveryInfo?.Instructions && <Row>Delivery Instructions: {deliveryInfo?.Instructions}</Row>}
                <Detail>
                  {activeCustomer?.IsDelivery && (
                    <LinkButton label='View Manifest' onClick={() => getAndShowManifest()} />
                  )}
                </Detail>
              </SecondaryDetailsContainer>
            </DetailsContainer>

            <Row>
              <Button onClick={() => setViewProfileDrawerOpen(true)} secondary fullWidth>
                View profile
              </Button>
            </Row>
          </BottomContainer>
        )}

        {!isShowDriversLicenseEnabled && (
          <>
            <Row>
              {activeCustomer?.Phone && (
                <>
                  Phone #: <a href={`tel:${activeCustomer?.Phone}`}>{activeCustomer?.Phone}</a>{' '}
                </>
              )}
              {activeCustomer?.IsDelivery && (
                <LinkButton label='View Manifest' sx={{ float: 'right' }} onClick={() => getAndShowManifest()} />
              )}
            </Row>
            <Row>DOB: {dob}</Row>
            <Row>TxnID: {shipmentId}</Row>
            {selectedPath === Path.Delivery && (
              <Row>
                <a href={addressUrl}>{getFormattedAddress(deliveryInfo)}</a>
              </Row>
            )}
            {deliveryInfo?.Instructions && <Row>Delivery Instructions: {deliveryInfo?.Instructions}</Row>}
          </>
        )}
      </Container>

      {isShowDriversLicenseEnabled && (
        <ViewProfileDrawer
          isOpen={isViewProfileDrawerOpen}
          onClose={() => setViewProfileDrawerOpen(false)}
          profile={{
            name: activeCustomer?.FullName,
            phone: activeCustomer?.Phone,
            dob,
            txnId: shipmentId,
            address: getFormattedAddress(deliveryInfo),
            addressUrl,
            idImage,
            deliveryInstructions: deliveryInfo?.Instructions,
          }}
        />
      )}
    </>
  );
};

const Container = styled.div`
  margin-bottom: 0.5rem;
`;

const DetailsContainer = styled.div`
  display: flex;
  gap: 16px;
`;

const BottomContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Row = styled.div`
  color: var(--color-gray-90);
  font-size: 1rem;
  line-height: 162%;

  & > a {
    color: var(--color-blue-60);
    text-decoration: underline;
  }
`;

const Detail = styled.div`
  font-size: 16px;
  color: ${colors.accentGrey35};

  & > button {
    padding: 0;
  }
`;

const Image = styled.img`
  width: 100px;
  height: 100px;
  border-radius: 8px;
  object-fit: cover;
  object-position: center;
  display: block;
  flex-shrink: 0;
`;

const PlaceholderBox = styled.div`
  height: 100px;
  width: 100px;
  border-radius: 8px;
  background-color: ${colors.gray20};
  border: 1px solid ${colors.gray30};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const SecondaryDetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;
